import {
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSTableRowMenuAction
} from "@wingspanhq/fe-component-library";
import React from "react";

import {
  RequirementAdditionalData,
  RequirementContextType,
  RequirementTableItemData
} from "../types";
import { useRequirementsConfig } from "../useRequirementsConfig";
import { getIsGeneralRequirement, getRequirementListKey } from "../utils";

export type RequirementsTableProps = {
  requirements: Array<RequirementTableItemData>;
  contextType: RequirementContextType;
  hideActions?: boolean;
  compact?: boolean;
  requiredAction?: string;
  onViewDetails?: (
    item: RequirementTableItemData,
    additionalData?: RequirementAdditionalData
  ) => void;
};

export const RequirementsTable: React.FC<RequirementsTableProps> = ({
  requirements,
  contextType,
  hideActions,
  compact,
  onViewDetails,
  requiredAction
}) => {
  const tableConfig = useRequirementsConfig({
    requirements,
    contextType
  });

  const tableData: WSTableItem<RequirementTableItemData>[] = requirements.map(
    (requirement, index) => ({
      id: getRequirementListKey(requirement, index),
      data: requirement
    })
  );

  const columns: Array<WSTableColumn<RequirementTableItemData>> = [
    {
      config: {
        header: {
          text: "Requirement"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => (
        <WSTableCell {...tableConfig.getNameCellProps(rowItem.data)} />
      )
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: compact ? ["XS", "S", "M", "L", "XL"] : ["XS"],
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => (
        <WSTableCell {...tableConfig.getTypeCellProps(rowItem.data)} />
      )
    },
    {
      config: {
        header: {
          text: "Status"
        },
        gridTemplateSizeMax: "0.5fr"
      },
      renderFunction: rowItem => (
        <WSTableCell {...tableConfig.getStatusCellProps(rowItem.data)} />
      )
    }
  ];

  return (
    <WSTable<RequirementTableItemData>
      key={tableConfig.dataUpdatedAt}
      columns={columns}
      data-testid="requirementsTable"
      getOnRowClick={
        onViewDetails
          ? ({ data }) => {
              if (getIsGeneralRequirement(data)) {
                return undefined;
              }

              return ({ data }) => {
                const additionalData = tableConfig.getAdditionalData(data);

                onViewDetails(data, additionalData);
              };
            }
          : undefined
      }
      loading={tableConfig.isLoading}
      rowActions={({ data }) => {
        if (hideActions || !requiredAction) {
          return [];
        }

        const isActionRequired = tableConfig.isActionRequired(data);

        if (isActionRequired && onViewDetails) {
          return [
            {
              text: requiredAction,
              onClick: () => {
                const additionalData = tableConfig.getAdditionalData(data);
                onViewDetails(data, additionalData);
              }
            }
          ];
        }

        return [];
      }}
      rowMenuActions={({ data }) =>
        hideActions
          ? []
          : [
              ...(!getIsGeneralRequirement(data) && onViewDetails
                ? [
                    {
                      label: "View requirement",
                      icon: "view",
                      onClick: () => {
                        const additionalData =
                          tableConfig.getAdditionalData(data);

                        if (onViewDetails) {
                          onViewDetails(data, additionalData);
                        }
                      }
                    } as WSTableRowMenuAction<RequirementTableItemData>
                  ]
                : []),
              ...tableConfig.renderRowMenuActions(data)
            ]
      }
      tableData={tableData}
    />
  );
};
